import Class from '@/Class';
import { ICoreFrameGridBindingParameter, IVerticalResizeOptions } from '#/DefaultViewExtensionInterfaces';
import GridResizeStorageManager from '@/Utility/GridResizeStorageManager';

export default class GridVerticalResizeManager extends Class {
    private autoHeightDependencies: [KnockoutObservable<any>, (val: any) => boolean][];
    private resizeStorageManager?: GridResizeStorageManager;
    private gridParams: Partial<ICoreFrameGridBindingParameter>;
    private isHeightCustomizedByUser: KnockoutObservable<boolean>;

    constructor(private verticalResize: IVerticalResizeOptions) {
        super();

        if (!verticalResize)
            throw 'No vertical resize options provided!';

        this.isDisposable = true;
        this.autoHeightDependencies = [];
        this.resizeStorageManager = this.createResizeStorageManager();
        this.isHeightCustomizedByUser = this.observableOrDefault(false);
    }

    private createResizeStorageManager(): GridResizeStorageManager | undefined {
        const storageKeyPrefix = this.verticalResize.storageKeyPrefix;
        if (!storageKeyPrefix)
            return undefined;

        return new GridResizeStorageManager(storageKeyPrefix);
    }

    public manage(gridParams: Partial<ICoreFrameGridBindingParameter>) {
        this.gridParams = gridParams;
        gridParams.isHeightUserAdjustable = this.verticalResize.isUserResizeEnabled;

        this.isHeightCustomizedByUser = this.resizeStorageManager?.manage(gridParams) ?? this.observableOrDefault(false);

        if (this.verticalResize.autoResize) {
            this.setupAutoResize(this.isHeightCustomizedByUser);
        }
    }

    public resetUserStorageAndTriggerRecalculation(): void {
        this.resizeStorageManager.resetUserData();

        this.isHeightCustomizedByUser(false);

        if (this.gridParams.autoHeight) {
            this.gridParams.autoHeight(this.verticalResize.autoResize());
            this.triggerAutoHeightRecalculation();
        }
    }

    /**
     * Registriert eine Abhängigkeit für die automatische Höhenberechnung.
     * @param dependency Die Abhängigkeit
     * @param stopRecalculation Eine Funktion, die anhand des Wertes der Abhängigkeit bestimmt, ob die Höhenberechnung abgebrochen werden soll
     */
    public registerAutoHeightDependency<T>(dependency: KnockoutObservable<T>, stopRecalculation: (val: T) => boolean) {
        this.autoHeightDependencies.push([dependency, stopRecalculation]);
    }

    /**
     * Aktiviert die Funktion, dass das Grid seine Höhe bis zu einer bestimmten Anzahl an Zeilen automatisch anpasst.
     * Diese Funktion greift nur, solange der User die Höhe noch nicht "per Hand" verändert hat.
     */
    private setupAutoResize(isHeightCustomizedByUser: KnockoutObservable<boolean>) {
        this.gridParams.autoHeight = this.observableOrDefault(undefined);
        const autoResize = this.observableOrDefault(this.verticalResize.autoResize);
        this.defineSubscription(autoResize, value => {
            if (isHeightCustomizedByUser())
                return;

            this.gridParams.autoHeight(value);
        })(autoResize());

        const autoHeightTrigger: KnockoutComputed<boolean> = this.defineComputed(() => {
            const customizedByUser = isHeightCustomizedByUser();

            let anyDependencyStopsRecalculation = false;
            for (const [dependency, stopRecalculation] of this.autoHeightDependencies) {
                const value = dependency();
                anyDependencyStopsRecalculation = anyDependencyStopsRecalculation || stopRecalculation(value);
            }
            if (anyDependencyStopsRecalculation) {
                return false;
            }

            // Falls das Grid erst irgendwann nach der Initialisierung vom User customized wird,
            // darf ab diesem Zeitpunkt die Höhe nicht automatisch neu berechnet werden.
            return !customizedByUser;
        }).extend({ notify: 'always' });

        const debouncedTriggerAutoHeightRecalculation = _.debounce(this.triggerAutoHeightRecalculation.bind(this), 10);
        this.defineSubscription(autoHeightTrigger, (recalculate) => {
            if (!recalculate)
                return;

            debouncedTriggerAutoHeightRecalculation();
        })(autoHeightTrigger());
    }

    private triggerAutoHeightRecalculation() {
        // Auch wenn sich die Werte von autoHeight nicht geändert haben, geben wir dem Grid bescheid, dass es seine
        // Höhe neu berechnen soll. Durch valueHasMutated wird der Aufruf der Grid-Methode im Binding angestoßen.
        this.gridParams?.autoHeight?.valueHasMutated();
    }
}